import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  LayoutDashboard,
  CreditCard,
  Users,
  Store,
  FileText,
  Shield,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  Bell,
  User,
  BarChart3,
  Building2,
  Terminal,
  Briefcase,
  Percent,
  ClipboardList,
  TrendingUp,
  Webhook,
  Globe,
  Wallet,
  UserCircle,
} from 'lucide-react';

import NotificationsDropdown from './NotificationsDropdown';
import LanguageSwitcher from './LanguageSwitcher';
import { useLanguage } from '../context/LanguageContext';

const Layout = ({ children }) => {
  const { user, logout, hasPermission } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Check if user is a merchant (logged in via merchant-login)
  const isMerchantView = !!localStorage.getItem('impersonate_token') && !user;
  const impersonateMerchant = isMerchantView ? JSON.parse(localStorage.getItem('impersonate_merchant') || '{}') : null;

  // Navigation for admins
  const adminNavigation = [
    { name: t('nav.dashboard'), href: '/', icon: LayoutDashboard },
    { name: t('nav.payments'), href: '/payments', icon: CreditCard, permission: 'payments' },
    { name: 'Выплаты', href: '/payouts', icon: Wallet, permission: 'payouts' },
    { name: t('nav.merchants'), href: '/merchants', icon: Store, permission: 'merchants' },
    { name: t('nav.providers'), href: '/providers', icon: Building2, permission: 'merchants' },
    { name: t('nav.terminals'), href: '/terminals', icon: Terminal, permission: 'merchants' },
    { name: t('nav.commissions'), href: '/commissions', icon: Percent, permission: 'merchants' },
    { name: t('nav.reports'), href: '/reports', icon: BarChart3, permission: 'reports' },
    { name: t('nav.analytics'), href: '/analytics', icon: TrendingUp, permission: 'reports' },
    { name: t('nav.aml'), href: '/aml', icon: Shield, permission: 'aml' },
    { name: t('nav.audit'), href: '/audit', icon: ClipboardList, permission: 'settings' },
    { name: t('nav.users'), href: '/users', icon: Users, permission: 'users' },
    { name: t('nav.notifications'), href: '/notification-settings', icon: Bell, permission: 'settings' },
    { name: t('nav.webhooks'), href: '/webhooks', icon: Webhook, permission: 'settings' },
  ];

  // Navigation for merchants - only cabinet
  const merchantNavigation = [
    { name: 'Личный кабинет', href: '/cabinet', icon: Briefcase },
  ];

  const handleLogout = () => {
    if (isMerchantView) {
      // Merchant logout - clear impersonate tokens
      localStorage.removeItem('impersonate_token');
      localStorage.removeItem('impersonate_merchant');
      navigate('/merchant-login');
    } else {
      logout();
      navigate('/login');
    }
  };

  // Select navigation based on user type
  const navigation = isMerchantView ? merchantNavigation : adminNavigation;

  const filteredNav = navigation.filter((item) => {
    if (isMerchantView) return true; // Merchants see all their allowed items
    if (!item.permission) return true;
    return hasPermission(item.permission, 'read');
  });

  // Get display name
  const displayName = isMerchantView 
    ? impersonateMerchant?.name || 'Мерчант'
    : user?.name || 'User';

  const displayRole = isMerchantView ? 'Мерчант' : user?.role;

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-slate-800 border-r border-slate-700 transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700">
          <Link to={isMerchantView ? '/cabinet' : '/'} className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-emerald-400 to-cyan-500 rounded-lg flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold text-white">OxyPay</span>
          </Link>
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden text-slate-400"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {filteredNav.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
                }`}
                data-testid={`nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.name}</span>
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isMerchantView 
                ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                : 'bg-gradient-to-br from-emerald-500 to-cyan-500'
            }`}>
              <span className="text-white font-medium">
                {displayName?.charAt(0)?.toUpperCase() || 'M'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{displayName}</p>
              <p className="text-xs text-slate-400 truncate">{displayRole}</p>
            </div>
          </div>
          <Button
            variant="outline"
            className="w-full border-slate-600 text-slate-300 hover:bg-slate-700"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t("auth.logout")}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="sticky top-0 z-30 h-16 bg-slate-800/80 backdrop-blur-xl border-b border-slate-700 flex items-center justify-between px-4">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden text-slate-400"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>

          <div className="flex-1" />

          <div className="flex items-center gap-3">
            {!isMerchantView && (
              <>
                <LanguageSwitcher className="hidden sm:flex" />
                <NotificationsDropdown />
              </>
            )}
            <div className="hidden sm:flex items-center gap-2 text-sm text-slate-300">
              <User className="w-4 h-4" />
              {displayName}
              {isMerchantView && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-400">
                  Мерчант
                </span>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
